import React, { useState } from "react";
import { Box, Button } from "@mantine/core";
import { IconAdjustmentsHorizontal } from "@tabler/icons-react";
import { MultiSelectFilterMenu } from "../../common/FilterChip";
import {
  BaseViewConfig,
  ReportFacetOption,
  ReportV2Definition,
} from "../../../types/reportsV2";
import { useFacetOptions } from "./hooks/useFacetOptions";
import { BaseViewPatch, buildToolbarFilters } from "./model/toolbarFilters";
import AddFilterMenu from "./AddFilterMenu";
import ReportDateChip from "./ReportDateChip";
import { COMPACT_BUTTON_PROPS } from "./compactButton";
import styles from "./ReportCanvas.module.css";

export interface ReportToolbarProps {
  definition: ReportV2Definition;
  view: BaseViewConfig;
  /** Filter options from the latest run. */
  facets?: Record<string, ReportFacetOption[]>;
  /** Resolved dates of the latest run (date chip hint). */
  resolvedRange?: { preset: string; start: string; end: string } | null;
  /** Hidden filters (e.g. staff for an own-scoped Earnings Summary). */
  hiddenFilters?: string[];
  onUpdate: (patch: BaseViewPatch) => void;
  /** Customize toggle; omitted for reports with nothing to customize
   *  (summaries). */
  customizeOpen?: boolean;
  onToggleCustomize?: () => void;
  /** Extra buttons at the right end (e.g. a summary's Print). */
  endSection?: React.ReactNode;
}

/**
 * View toolbar: date range, a chip per shown filter (options from the run's
 * facets; optional chips can be removed), "+ Filter" for the rest, then any
 * extra buttons and the Customize toggle (table reports). Every change edits
 * the draft view. Shared by table and summary reports.
 */
export default function ReportToolbar({
  definition,
  view,
  facets,
  resolvedRange,
  hiddenFilters,
  onUpdate,
  customizeOpen = false,
  onToggleCustomize,
  endSection,
}: ReportToolbarProps) {
  const optionsFor = useFacetOptions(facets);
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const filters = buildToolbarFilters(
    definition,
    view,
    onUpdate,
    hiddenFilters,
  );

  // A chip added from "+ Filter" opens its menu right away.
  const showFilter = (key: string) => {
    setJustAdded(key);
    filters.showFilter(key);
  };

  return (
    <Box className={styles.toolbar}>
      <ReportDateChip
        range={view.range}
        presets={definition.date_range_presets}
        defaultRange={definition.default_view.range}
        resolved={resolvedRange}
        onChange={(range) => onUpdate({ range })}
      />
      {filters.shown.map((f) => {
        const selected = view.filters[f.key] ?? [];
        return (
          <MultiSelectFilterMenu
            key={f.key}
            variant="outline"
            // A long selection ellipsizes so one chip can't stretch the bar.
            valueMaxWidth="var(--chip-value-max-width)"
            label={f.label}
            options={optionsFor(f.key, selected)}
            selected={selected}
            onChange={(values) => filters.setFilter(f.key, values)}
            onRemove={
              f.always_shown ? undefined : () => filters.hideFilter(f.key)
            }
            defaultOpened={justAdded === f.key}
          />
        );
      })}
      <AddFilterMenu filters={filters.addable} onAdd={showFilter} />
      <Box className={styles.spacer} />
      {endSection}
      {onToggleCustomize && (
        <Button
          {...COMPACT_BUTTON_PROPS}
          variant={customizeOpen ? "light" : "default"}
          color={customizeOpen ? "teal" : undefined}
          leftSection={<IconAdjustmentsHorizontal size={14} aria-hidden />}
          onClick={onToggleCustomize}
          aria-expanded={customizeOpen}
        >
          Customize
        </Button>
      )}
    </Box>
  );
}
